import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, ArrowRight, Camera } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { trackWorkshopEvent } from '@/utils/workshopAnalytics';

interface NearbyWorkshop {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  price_display: string | null;
  workshop_type: string;
  workshop_dates: Array<{ start_date: string; end_date: string }>;
  workshop_photos: Array<{ photo_url: string; sort_order: number }>;
  workshop_leaders: { business_name: string } | null;
  distance_miles?: number;
}

interface Props {
  latitude: number;
  longitude: number;
  locationName: string;
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export const NearbyWorkshops: React.FC<Props> = ({ latitude, longitude, locationName }) => {
  const [workshops, setWorkshops] = useState<NearbyWorkshop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNearby();
  }, [latitude, longitude]);

  const loadNearby = async () => {
    setLoading(true);
    try {
      // Use the haversine function to find nearby workshops
      const { data: nearbyIds, error: nearbyError } = await supabase
        .rpc('workshops_near_location', {
          search_lat: latitude,
          search_lon: longitude,
          radius_miles: 100,
        });

      if (nearbyError || !nearbyIds || nearbyIds.length === 0) {
        setWorkshops([]);
        setLoading(false);
        return;
      }

      const ids = nearbyIds.map((r: { listing_id: string; distance_miles: number }) => r.listing_id);
      const distanceMap = new Map(nearbyIds.map((r: { listing_id: string; distance_miles: number }) => [r.listing_id, r.distance_miles]));

      // Fetch full listing data for nearby workshops
      const now = new Date().toISOString().split('T')[0];
      const { data: listings, error: listingsError } = await supabase
        .from('workshop_listings')
        .select(`
          id, slug, title, location_name, price_display, workshop_type,
          workshop_dates(start_date, end_date),
          workshop_photos(photo_url, sort_order),
          workshop_leaders(business_name)
        `)
        .in('id', ids)
        .eq('is_active', true);

      if (listingsError) throw listingsError;

      // Filter to only workshops with future dates and add distance
      const withFutureDates = (listings || [])
        .filter(l => l.workshop_dates?.some((d: { end_date: string }) => d.end_date >= now))
        .map(l => ({
          ...l,
          distance_miles: distanceMap.get(l.id) || 0,
        }))
        .sort((a, b) => (a.distance_miles || 0) - (b.distance_miles || 0));

      setWorkshops(withFutureDates);
    } catch (err) {
      console.error('Error loading nearby workshops:', err);
      setWorkshops([]);
    } finally {
      setLoading(false);
    }
  };

  // Track nearby impressions once workshops are loaded
  useEffect(() => {
    if (workshops.length > 0) {
      workshops.slice(0, 4).forEach(w => {
        trackWorkshopEvent('nearby_impression', {
          workshopId: w.id,
          source: 'nearby',
          metadata: { location: locationName },
        });
      });
    }
  }, [workshops.map(w => w.id).join(',')]);

  // Don't render anything if loading or no results
  if (loading || workshops.length === 0) return null;

  const getNextDate = (dates: Array<{ start_date: string; end_date: string }>) => {
    const now = new Date().toISOString().split('T')[0];
    return dates
      .filter(d => d.end_date >= now)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
  };

  return (
    <div className="mt-6">
      {/* Compact banner */}
      <Link
        to={`/workshops?q=${encodeURIComponent(locationName)}`}
        className="flex items-center gap-3 p-3 rounded-[4px] border border-border bg-muted/30 hover:bg-muted/60 transition-colors group"
      >
        <Camera className="w-5 h-5 text-[#c9922a] flex-shrink-0" />
        <span className="text-sm text-foreground">
          <strong>{workshops.length} workshop{workshops.length !== 1 ? 's' : ''}</strong>
          {' '}listed near {locationName}
        </span>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors ml-auto flex-shrink-0" />
      </Link>

      {/* Full cards below */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        {workshops.slice(0, 4).map((workshop) => {
          const heroPhoto = workshop.workshop_photos?.sort((a, b) => a.sort_order - b.sort_order)[0];
          const nextDate = getNextDate(workshop.workshop_dates || []);

          return (
            <Link
              key={workshop.id}
              to={`/workshops/${workshop.slug}`}
              className="group"
              onClick={() => trackWorkshopEvent('card_click', {
                workshopId: workshop.id,
                source: 'nearby',
                allowDuplicate: true,
              })}
            >
              <Card className="overflow-hidden rounded-[4px] hover:shadow-md transition-shadow">
                <div className="flex">
                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 bg-muted">
                    {heroPhoto ? (
                      <img src={heroPhoto.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-6 h-6 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <CardContent className="p-3 flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm leading-tight truncate group-hover:text-[#c9922a] transition-colors">
                      {workshop.title}
                    </h4>
                    {workshop.workshop_leaders && (
                      <p className="text-xs text-muted-foreground mt-0.5">{workshop.workshop_leaders.business_name}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      {nextDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(nextDate.start_date), 'MMM d')}
                        </span>
                      )}
                      {workshop.distance_miles !== undefined && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {Math.round(workshop.distance_miles)}mi
                        </span>
                      )}
                      {workshop.price_display && (
                        <span className="font-medium text-foreground">{workshop.price_display}</span>
                      )}
                    </div>
                  </CardContent>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
